import { Grid, Box } from '@chakra-ui/react'
import { FC, useMemo, useEffect } from 'react'
import { AiOutlineLineChart } from 'react-icons/ai'
import { BiCreditCardAlt, BiPackage } from 'react-icons/bi'
import { RiSecurePaymentLine } from 'react-icons/ri'

import useMrpScheduledPurchase from '@hook/mrp/use-scheduled-purchase'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Layout from '@component/Layout'
import InfoCard from '@component/Layout/Card/Info'
import CardSlider from '@component/Layout/CardSlider'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import TableGrid from '@component/Layout/TableGrid'
import GridQuantity from '@component/Layout/TableGrid/GridQuantity'
import LayoutTools from '@component/Layout/Tools'

import { validateAppVersion } from '@helper/util/storangeClear'
import generic from '@service/generic'

const MrpScheduledPurchase: FC = () => {
  const { menuToggle } = useLayout()
  const { getFilters } = useFilters()

  const {
    loading,
    fetching,
    headers,
    actions,
    tableInteraction,
    calc,
    data,
    filename,
    params,
    query,
    filters,
    handleLocalParams,
    currentyDate,
    setFilters,
    defaoutDesiredPeriod,
    setFilename,
    setQuery,
    getData,
    getSupplyCommands,
    filtersList,
    handleUpdateParams,
    clearAllFilters
  } = useMrpScheduledPurchase()

  useEffect(() => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        validateAppVersion(data.version)

        if (data.valor === 'true') {
          window.location.href = '/manutencao'
        } else {
          getFilters({
            companies: true,
            worlds: true,
            centers: true,
            providersForDistribution: true,
            categoryManagers: true,
            productManagers: true,
            compactCategories: true,
            categories: true,
            providersForPurchase: true,
            abcCurves: true,
            subCategories: true
          })

          getSupplyCommands()
          handleLocalParams()
        }
      })
      .catch(() => {})

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { hasSessions } = useSessions()

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 142px)',
    [menuToggle, hasSessions]
  )

  const tableHeight = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 272px)'
          : 'calc(100vh - 241px)'
        : hasSessions
        ? 'calc(100vh - 413px)'
        : 'calc(100vh - 382px)',
    [menuToggle, hasSessions]
  )

  return (
    <Layout title="MRP Compra Programada">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!filters ? '100vw' : '260px calc(100vw - 260px)'}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            values={params}
            onChange={handleUpdateParams}
            loading={loading}
            defaultDate={currentyDate}
            canCancel={fetching}
            onFilter={getData}
            changedValue={defaoutDesiredPeriod}
            onClearAllFilters={clearAllFilters}
          />
        )}
        <Box>
          <LayoutTools
            title={filename}
            filters={filters}
            actions={actions}
            query={query}
            onSearchChange={setQuery}
            onTitleChange={setFilename}
            toggleFilters={setFilters.toggle}
            isDisabled={loading || fetching || data.length === 0}
          />

          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros no banco de dados"
            />
          ) : data.length === 0 ? (
            <LayoutEmpty
              title="Nada encontrado!"
              message="Filtre sua busca ao lado para obter informações"
            />
          ) : (
            <>
              <CardSlider isFilters={filters}>
                <InfoCard
                  value={calc.skusDistintos}
                  title="SKUs distintos"
                  tooltip="Quantidade de SKUs distintos com sugestão de compra maior que zero"
                  icon={<BiPackage color="#1459B4 " />}
                  decimals={1}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={calc.custoRecomendado}
                  title="Custo recomendado"
                  icon={<RiSecurePaymentLine color="#C22C31" />}
                  decimals={1}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={calc.volumeTotalDeCompra}
                  title="Volume total de compra"
                  icon={<AiOutlineLineChart color="#308AFF" />}
                  decimals={1}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={calc.custoTotalDeCompra}
                  title="Custo total de compra"
                  icon={<BiCreditCardAlt color="#24B85F" />}
                  decimals={1}
                  isLoading={fetching}
                  noShadow
                />
              </CardSlider>

              <GridQuantity data={data} disabled={data.length === 0} />

              <TableGrid
                id="purschase-table"
                headers={headers}
                query={query}
                data={data}
                loading={fetching}
                actions={tableInteraction}
                height={tableHeight}
                itsSortable={true}
                entity="mrp-purchase"
              />
            </>
          )}
        </Box>
      </Grid>
    </Layout>
  )
}

export default MrpScheduledPurchase
